/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  Heading,
  ModalAlert,
  Spinner,
} from '@sonarsource/echoes-react';
import { noop } from 'lodash';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface SandboxSettingWrapperProps {
  additionalButtons?: ReactNode;
  children: ReactNode;
  doSubmit: () => void;
  hasAddedCriticalTypesFlag: boolean;
  isChanged: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  onReset: () => void;
  onSubmit: () => Promise<void>;
}

export function SandboxSettingWrapper({
  additionalButtons,
  children,
  doSubmit,
  hasAddedCriticalTypesFlag,
  isChanged,
  isLoading,
  isUpdating,
  onReset,
  onSubmit,
}: Readonly<SandboxSettingWrapperProps>) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleSubmit = async () => {
    try {
      await onSubmit();

      if (hasAddedCriticalTypesFlag) {
        setConfirmDialogOpen(true);
        return;
      }

      doSubmit();
    } catch {
      noop();
    }
  };

  const handleConfirm = () => {
    doSubmit();
    setConfirmDialogOpen(false);
  };

  return (
    <section className="sw-p-6 sw-max-w-[650px]">
      <Spinner isLoading={isLoading}>
        <Heading as="h4">
          <FormattedMessage id="settings.sandbox_issues.title" />
        </Heading>

        {children}

        {(isChanged || additionalButtons) && (
          <div className="sw-mt-6">
            <ButtonGroup>
              {isChanged && (
                <>
                  <Button
                    isDisabled={isUpdating}
                    isLoading={isUpdating}
                    onClick={handleSubmit}
                    variety={ButtonVariety.Primary}
                  >
                    <FormattedMessage id="save" />
                  </Button>
                  <Button onClick={onReset}>
                    <FormattedMessage id="cancel" />
                  </Button>
                </>
              )}
              {additionalButtons}
            </ButtonGroup>
          </div>
        )}
      </Spinner>

      <ModalAlert
        content={<FormattedMessage id="settings.sandbox_issues.confirm.question" />}
        description={<FormattedMessage id="settings.sandbox_issues.confirm.description" />}
        isOpen={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        primaryButton={
          <Button
            isDisabled={isUpdating}
            isLoading={isUpdating}
            onClick={handleConfirm}
            variety={ButtonVariety.Primary}
          >
            <FormattedMessage id="yes" />
          </Button>
        }
        secondaryButtonLabel={<FormattedMessage id="cancel" />}
        title={<FormattedMessage id="settings.sandbox_issues.confirm.title" />}
      />
    </section>
  );
}
